import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Camera, Check, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/api";
import { waLink } from "@/lib/company";
import { usePageMeta } from "@/lib/seo";

const steps = ["Fahrzeug", "Kilometer", "Fotos", "Beschreibung", "Kontakt"];

const BRANDS = ["Porsche", "Mercedes-Benz", "BMW", "Audi", "Ferrari", "Lamborghini", "Maserati", "Bentley", "Aston Martin", "McLaren", "Land Rover", "Volkswagen", "Andere Marke"];
const FUELS = ["Benzin", "Diesel", "Hybrid (Benzin/Elektro)", "Elektro", "Sonstiges"];
const GEARS = ["Automatik", "Schaltgetriebe", "Halbautomatik"];
const YEARS = Array.from({ length: 2026 - 1989 }, (_, i) => String(2026 - i));

export default function Ankauf() {
  usePageMeta("Fahrzeugankauf", "Verkaufen Sie Ihr Fahrzeug an AD MOTORS – fair, schnell und unkompliziert.");
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noVin, setNoVin] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [checking, setChecking] = useState(false);
  const [decoded, setDecoded] = useState(null);
  const [vinMsg, setVinMsg] = useState("");
  const [d, setD] = useState({
    kennzeichen: "", brand: "", model: "", year: "", fuel: "", transmission: "", power_ps: "",
    km: "", photos: [], beschreibung: "",
    name: "", email: "", phone: "",
  });

  const vehicleValid = noVin
    ? d.brand.length > 0 && d.model.trim().length >= 2 && d.year.length > 0
    : d.kennzeichen.trim().length >= 3;

  const canNext = [
    vehicleValid,
    d.km.trim().length >= 1,
    true,
    true,
    d.name.trim().length >= 2 && (d.email.trim().length >= 5 || d.phone.trim().length >= 5),
  ][step];

  const vehicleLabel = noVin ? `${d.brand} ${d.model} (${d.year})`.trim() : d.kennzeichen;

  const cleanVin = d.kennzeichen.replace(/[\s-]/g, "").toUpperCase();
  const isVinLike = /^[A-HJ-NPR-Z0-9]{17}$/.test(cleanVin);

  const applyDecoded = (data) => {
    const marke = data.data.marke || "";
    const matched = BRANDS.find((b) => b.toLowerCase() === marke.toLowerCase())
      || BRANDS.find((b) => b !== "Andere Marke" && (marke.toLowerCase().includes(b.toLowerCase()) || b.toLowerCase().includes(marke.toLowerCase())));
    setD((prev) => ({
      ...prev,
      ...(data.data.vin ? { kennzeichen: data.data.vin } : {}),
      brand: matched || (marke ? "Andere Marke" : prev.brand),
      model: matched ? (data.data.modell || "") : `${marke} ${data.data.modell || ""}`.trim(),
      year: YEARS.includes(String(data.data.baujahr)) ? String(data.data.baujahr) : "",
      fuel: FUELS.includes(data.data.kraftstoff) ? data.data.kraftstoff : "",
      transmission: GEARS.includes(data.data.getriebe) ? data.data.getriebe : "",
      power_ps: data.data.leistung_ps || "",
    }));
    setDecoded(data);
    setNoVin(true);
  };

  const checkVin = async () => {
    setChecking(true);
    setVinMsg("");
    try {
      const { data } = await api.get(`/vin/decode/${cleanVin}`);
      if (data.found) {
        applyDecoded(data);
      } else {
        setVinMsg("Die VIN konnte nicht automatisch decodiert werden – kein Problem, unser Team prüft sie manuell. Sie können direkt fortfahren oder die Daten selbst angeben.");
      }
    } catch {
      setVinMsg("Die Prüfung ist gerade nicht verfügbar. Sie können trotzdem fortfahren – wir prüfen die VIN manuell.");
    }
    setChecking(false);
  };

  const handlePhotos = async (e) => {
    const files = [...e.target.files];
    e.target.value = "";
    if (!files.length) return;
    setUploading(files.length);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/leads/upload", fd);
        setD((prev) => ({ ...prev, photos: [...prev.photos, `${BACKEND_URL}${data.url}`] }));
      } catch {
        toast.error(`Upload fehlgeschlagen: ${file.name}`);
      }
      setUploading((u) => u - 1);
    }
  };

  const removePhoto = (idx) => setD({ ...d, photos: d.photos.filter((_, i) => i !== idx) });

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/leads", {
        type: "ankauf", name: d.name, email: d.email, phone: d.phone,
        message: `Ankauf-Anfrage: ${vehicleLabel}`,
        data: noVin
          ? {
              ...(d.kennzeichen.trim() ? { kennzeichen_vin: d.kennzeichen } : {}),
              marke: d.brand, modell: d.model, baujahr: d.year,
              kraftstoff: d.fuel, getriebe: d.transmission, leistung_ps: d.power_ps,
              kilometer: d.km, fotos: d.photos, beschreibung: d.beschreibung,
            }
          : {
              kennzeichen_vin: d.kennzeichen, kilometer: d.km,
              fotos: d.photos, beschreibung: d.beschreibung,
            },
      });
      setDone(true);
    } catch {
      toast.error("Anfrage konnte nicht gesendet werden.");
    }
    setLoading(false);
  };

  const inputCls = "w-full bg-surface border border-line text-ink placeholder:text-faint rounded-lg px-5 py-4 text-base outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  const labelCls = "text-[11px] uppercase tracking-wider text-faint font-semibold block mb-1.5";

  if (done)
    return (
      <div data-testid="ankauf-success" className="pt-32 pb-24 max-w-2xl mx-auto px-5 sm:px-8 text-center">
        <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand text-brand-ink mb-8"><Check size={28} /></span>
        <h1 className="font-display font-bold text-ink text-4xl sm:text-5xl tracking-tight mb-5">Vielen Dank!</h1>
        <p className="text-dim leading-relaxed max-w-md mx-auto">
          Ihre Ankauf-Anfrage ist bei uns eingegangen. Unser Team bewertet Ihr Fahrzeug
          und meldet sich innerhalb von 24 Stunden mit einem fairen Angebot.
        </p>
        <a href={waLink(`Hallo, ich habe soeben eine Ankauf-Anfrage für ${vehicleLabel} gesendet.`)}
          target="_blank" rel="noopener noreferrer" data-testid="ankauf-success-whatsapp"
          className="btn-pill inline-flex items-center gap-2 bg-[#25D366] text-[#06210f] font-semibold px-8 py-4 text-sm mt-10 hover:brightness-110">
          Direkt per WhatsApp weitersprechen
        </a>
      </div>
    );

  return (
    <div data-testid="ankauf-page" className="pt-28 md:pt-36 pb-24 max-w-2xl mx-auto px-5 sm:px-8">
      <p className="eyebrow mb-5">Fahrzeugankauf</p>
      <h1 className="font-display font-bold text-ink text-4xl sm:text-5xl tracking-tight mb-4">Verkaufen Sie Ihr Fahrzeug.</h1>
      <p className="text-dim mb-12">Fair, schnell und unkompliziert – in 5 Schritten zur kostenlosen Bewertung.</p>

      <div className="flex items-center gap-2 mb-12" data-testid="ankauf-progress">
        {steps.map((s, i) => (
          <div key={s} className="flex-1">
            <div className={`h-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-brand" : "bg-line"}`} />
            <p className={`text-[10px] mt-2 uppercase tracking-wider hidden sm:block ${i <= step ? "text-ink font-semibold" : "text-faint"}`}>{s}</p>
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={step}
          initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }} className="min-h-[220px]">
          {step === 0 && !noVin && (
            <div>
              <h2 className="font-display font-bold text-2xl text-ink mb-2">Fahrgestellnummer (VIN) oder Kennzeichen</h2>
              <p className="text-sm text-faint mb-6">Damit identifizieren wir Ihr Fahrzeug am genauesten – bei vollständiger VIN erkennen wir die Fahrzeugdaten automatisch.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input data-testid="ankauf-kennzeichen-input" autoFocus placeholder="z. B. WP0ZZZ99ZTS392124 oder MD-123AB"
                  value={d.kennzeichen} onChange={(e) => { setD({ ...d, kennzeichen: e.target.value }); setVinMsg(""); }} className={`${inputCls} flex-1`} />
                {isVinLike && (
                  <button type="button" data-testid="ankauf-vin-check-btn" onClick={checkVin} disabled={checking}
                    className="btn-pill inline-flex items-center justify-center gap-2 bg-action text-action-ink hover:bg-action-hover px-7 py-4 text-sm font-semibold disabled:opacity-50 whitespace-nowrap">
                    {checking ? <><Loader2 size={15} className="animate-spin" /> Wird geprüft…</> : <><Search size={15} /> VIN prüfen</>}
                  </button>
                )}
              </div>
              {vinMsg && <p data-testid="ankauf-vin-msg" className="text-sm text-dim mt-4 bg-elevated rounded-xl px-4 py-3">{vinMsg}</p>}
              <button type="button" data-testid="ankauf-no-vin-btn" onClick={() => setNoVin(true)}
                className="mt-5 block text-sm font-semibold underline underline-offset-4 text-dim hover:text-ink transition-colors">
                Ich habe keine VIN zur Hand – Fahrzeugdaten manuell angeben
              </button>
            </div>
          )}
          {step === 0 && noVin && (
            <div>
              <h2 className="font-display font-bold text-2xl text-ink mb-2">Ihr Fahrzeug</h2>
              {decoded ? (
                <div data-testid="ankauf-vin-decoded-banner" className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-3.5 mb-6">
                  <Check size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-700">Fahrzeug anhand der VIN erkannt</p>
                    <p className="text-xs text-emerald-700/80 mt-0.5">
                      Bitte prüfen und ergänzen Sie die vorausgefüllten Angaben.
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-faint mb-6">Je genauer Ihre Angaben, desto präziser unser Angebot.</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Marke *</label>
                  <select data-testid="ankauf-brand-select" value={d.brand} onChange={(e) => setD({ ...d, brand: e.target.value })} className={inputCls}>
                    <option value="">Bitte wählen…</option>
                    {BRANDS.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Modell *</label>
                  <input data-testid="ankauf-model-input" placeholder="z. B. 911 Carrera 4S" value={d.model}
                    onChange={(e) => setD({ ...d, model: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Baujahr / Erstzulassung *</label>
                  <select data-testid="ankauf-year-select" value={d.year} onChange={(e) => setD({ ...d, year: e.target.value })} className={inputCls}>
                    <option value="">Bitte wählen…</option>
                    {YEARS.map((y) => <option key={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Leistung (PS)</label>
                  <input data-testid="ankauf-power-input" type="number" placeholder="z. B. 450" value={d.power_ps}
                    onChange={(e) => setD({ ...d, power_ps: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Kraftstoff</label>
                  <select data-testid="ankauf-fuel-select" value={d.fuel} onChange={(e) => setD({ ...d, fuel: e.target.value })} className={inputCls}>
                    <option value="">Bitte wählen…</option>
                    {FUELS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Getriebe</label>
                  <select data-testid="ankauf-transmission-select" value={d.transmission} onChange={(e) => setD({ ...d, transmission: e.target.value })} className={inputCls}>
                    <option value="">Bitte wählen…</option>
                    {GEARS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <button type="button" data-testid="ankauf-has-vin-btn" onClick={() => { setNoVin(false); setDecoded(null); }}
                className="mt-5 text-sm font-semibold underline underline-offset-4 text-dim hover:text-ink transition-colors">
                Doch lieber VIN / Kennzeichen eingeben
              </button>
            </div>
          )}
          {step === 1 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-ink mb-6">Aktueller Kilometerstand</h2>
              <input data-testid="ankauf-km-input" autoFocus type="number" placeholder="z. B. 45000"
                value={d.km} onChange={(e) => setD({ ...d, km: e.target.value })} className={inputCls} />
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-ink mb-6">Fotos Ihres Fahrzeugs</h2>
              {d.photos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4" data-testid="ankauf-photo-previews">
                  {d.photos.map((url, i) => (
                    <div key={`${url}-${i}`} className="relative group">
                      <img src={url} alt="" loading="lazy" className="w-full h-20 object-cover rounded-xl bg-elevated" />
                      <button type="button" data-testid={`ankauf-remove-photo-${i}`} onClick={() => removePhoto(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-brand text-brand-ink flex items-center justify-center"
                        aria-label="Foto entfernen">
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label data-testid="ankauf-photos-input" className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-line rounded-2xl py-12 cursor-pointer hover:border-silver transition-colors">
                <Camera size={28} strokeWidth={1.5} className="text-faint" />
                <span className="text-sm font-medium">
                  {uploading > 0 ? `${uploading} Foto(s) werden hochgeladen…` : d.photos.length > 0 ? "Weitere Fotos hinzufügen" : "Fotos auswählen (optional)"}
                </span>
                <input type="file" accept="image/*" multiple className="hidden" disabled={uploading > 0} onChange={handlePhotos} />
              </label>
              <p className="text-xs text-faint mt-3">Die Fotos werden direkt mit Ihrer Anfrage übermittelt.</p>
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-ink mb-6">Beschreibung & Zustand</h2>
              <textarea data-testid="ankauf-beschreibung-input" autoFocus rows={5}
                placeholder="Ausstattung, Zustand, Unfälle, Servicehistorie… (optional)"
                value={d.beschreibung} onChange={(e) => setD({ ...d, beschreibung: e.target.value })}
                className={`${inputCls} resize-none`} />
            </div>
          )}
          {step === 4 && (
            <div className="space-y-4">
              <h2 className="font-display font-bold text-2xl text-ink mb-6">Ihre Kontaktdaten</h2>
              <input data-testid="ankauf-name-input" autoFocus placeholder="Name" value={d.name}
                onChange={(e) => setD({ ...d, name: e.target.value })} className={inputCls} />
              <input data-testid="ankauf-email-input" type="email" placeholder="E-Mail" value={d.email}
                onChange={(e) => setD({ ...d, email: e.target.value })} className={inputCls} />
              <input data-testid="ankauf-phone-input" placeholder="Telefon" value={d.phone}
                onChange={(e) => setD({ ...d, phone: e.target.value })} className={inputCls} />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-between mt-12">
        <button data-testid="ankauf-back-btn" onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="btn-pill inline-flex items-center gap-2 border border-line px-7 py-3.5 text-sm font-semibold disabled:opacity-30 hover:border-silver transition-colors">
          <ArrowLeft size={15} /> Zurück
        </button>
        {step < 4 ? (
          <button data-testid="ankauf-next-btn" onClick={() => setStep(step + 1)} disabled={!canNext || (step === 2 && uploading > 0)}
            className="btn-pill inline-flex items-center gap-2 bg-action text-action-ink hover:bg-action-hover px-8 py-3.5 text-sm font-semibold disabled:opacity-30 ">
            Weiter <ArrowRight size={15} />
          </button>
        ) : (
          <button data-testid="ankauf-submit-btn" onClick={submit} disabled={!canNext || loading}
            className="btn-pill inline-flex items-center gap-2 bg-action text-action-ink hover:bg-action-hover px-8 py-3.5 text-sm font-semibold disabled:opacity-30 ">
            {loading ? "Wird gesendet…" : "Anfrage senden"} <Check size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
